import React from 'react';
import { Search } from 'lucide-react';
import { useSearchCNJ } from '../../hooks/useSearchCNJ';

/**
 * Input de busca com detecção automática de números CNJ
 * @param {Object} props
 * @param {string} props.value - Valor atual do input
 * @param {function} props.onChange - Callback chamado quando o valor muda
 * @param {function} props.onSubmit - Callback chamado no submit (opcional)
 * @param {string} props.placeholder - Placeholder do input
 * @param {string} props.className - Classes CSS adicionais
 * @param {boolean} props.showCNJIndicator - Se deve mostrar indicador CNJ
 * @param {boolean} props.showTooltip - Se deve mostrar tooltip abaixo do input quando CNJ detectado
 * @returns {JSX.Element}
 */
const CNJSearchInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Buscar por processo, tribunal, classificação...",
  className = "",
  showCNJIndicator = true,
  showTooltip = true,
  ...props
}) => {
  const { isCNJNumber, cleanedTerm } = useSearchCNJ(value);
  
  const baseInputClasses = `
    w-full pl-10 pr-4 py-2 border rounded-lg 
    focus:ring-2 focus:ring-blue-500 focus:border-blue-500 
    text-gray-900 bg-white transition-colors
  `;
  
  const inputClasses = isCNJNumber 
    ? `${baseInputClasses} border-blue-300 bg-blue-50`
    : `${baseInputClasses} border-gray-300`;
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onSubmit) {
        onSubmit(e);
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            className={inputClasses}
            {...props}
          />
          
          {/* Indicador CNJ */}
          {showCNJIndicator && isCNJNumber && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  CNJ
                </span>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              </div>
            </div>
          )}
        </div>
      
      {/* Tooltip informativo */}
      {showCNJIndicator && showTooltip && isCNJNumber && (
        <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 z-10">
          <div className="flex items-center space-x-1">
            <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
            <span>
              Número CNJ detectado - busca será feita com: <code className="bg-blue-100 px-1 rounded">{cleanedTerm}</code>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CNJSearchInput;